import { Link } from 'react-router-dom';
import PublicPageShell, { FeatureGrid, BulletList, SectionHeading } from '@/components/PublicPageShell';
import { Lock, ShieldCheck, FileCheck, KeyRound, Database, History, UserCheck, AlertOctagon } from 'lucide-react';

const PROTECTIONS = [
  { icon: Lock,        t: 'Encryption in transit', d: 'All API and web traffic is TLS-encrypted end-to-end.' },
  { icon: Database,    t: 'POPIA-compliant storage', d: 'Personal information is stored with role-based access controls.' },
  { icon: UserCheck,   t: 'Minor data protection', d: 'U18 player data requires verified parental/guardian consent before activation.' },
  { icon: KeyRound,    t: 'Granular access control', d: 'Scouts, coaches, schools and admins see only what their role permits.' },
  { icon: FileCheck,   t: 'Multi-tier verification', d: 'Every player, coach, school, club and union goes through tiered identity verification.' },
  { icon: History,     t: 'Immutable audit log', d: 'Every admin and verification action is timestamped, IP-logged, and retained.' },
  { icon: ShieldCheck, t: 'Two-stage scout approval', d: 'Scouts undergo a two-stage approval before full database access is granted.' },
  { icon: AlertOctagon,t: 'Incident response', d: 'Documented breach notification procedure aligned to POPIA timelines.' },
];

export default function SecurityCompliance() {
  return (
    <PublicPageShell
      eyebrow="SECURITY & COMPLIANCE"
      title="Built for trust, designed for minors"
      intro="FutureXV holds data on developing rugby players — many of them minors. Every decision in the platform is shaped by that responsibility."
      dataTestId="security-page"
    >
      <FeatureGrid items={PROTECTIONS} cols="md:grid-cols-2" />

      <SectionHeading eyebrow="POPIA">South Africa's POPI Act compliance</SectionHeading>
      <div className="space-y-4 text-zinc-300 leading-relaxed">
        <p>
          The Protection of Personal Information Act (POPIA) sets out how personal data must be collected, stored,
          processed and shared in South Africa. FutureXV is built to those standards as a non-negotiable foundation.
        </p>
        <BulletList items={[
          "Explicit consent captured at signup (Terms + Privacy + marketing communication preferences).",
          "Each user can view, export and request deletion of their data from their profile settings.",
          "Sensitive fields (DOB, home address, contact numbers) are <strong>never</strong> shown on public profiles.",
          "Public-facing pages show first name + surname initial only for U18 players.",
          "All consent decisions are stored with timestamp, IP address and user-agent for auditability.",
          "Data is hosted within South African / EU regional infrastructure where feasible.",
        ]} />
      </div>

      <SectionHeading eyebrow="VERIFICATION SYSTEM">Multi-tier verification (Spec §34)</SectionHeading>
      <p className="text-zinc-400 mb-4">
        Every registrant goes through a tiered, email-based verification chain before their account is activated:
      </p>
      <BulletList items={[
        "<strong>Senior players</strong> → verified by their nominated coach.",
        "<strong>U18 players</strong> → dual approval required: <strong>both</strong> the coach <em>and</em> a parent/guardian must consent.",
        "<strong>Club coaches</strong> → verified by the registered club manager.",
        "<strong>School coaches</strong> → verified by the school's principal or headmaster.",
        "<strong>Union coaches</strong> → verified by a union administrator.",
        "Verification links are <strong>single-use</strong> and expire after <strong>7 days</strong>.",
        "Decisions capture IP address and user-agent, and are written to an immutable audit log.",
      ]} />

      <SectionHeading eyebrow="DOCUMENTS">Legal documents</SectionHeading>
      <div className="flex flex-wrap gap-3">
        <Link to="/privacy" className="border border-zinc-700 hover:border-[#A3FF00] text-zinc-200 hover:text-[#A3FF00] px-5 py-2.5 rounded-md transition-colors" data-testid="link-privacy">
          Privacy Policy
        </Link>
        <Link to="/terms" className="border border-zinc-700 hover:border-[#A3FF00] text-zinc-200 hover:text-[#A3FF00] px-5 py-2.5 rounded-md transition-colors" data-testid="link-terms">
          Terms of Service
        </Link>
        <a
          href="mailto:info@futurexv.co.za?subject=Data%20request"
          className="border border-zinc-700 hover:border-[#A3FF00] text-zinc-200 hover:text-[#A3FF00] px-5 py-2.5 rounded-md transition-colors"
          data-testid="link-data-request"
        >
          Request my data
        </a>
      </div>
    </PublicPageShell>
  );
}
